import React, { useEffect, useRef, useState } from "react";
import { useStore } from "react-redux";
import { Alert, AlertTitle, Box, Button, Divider } from "@mui/material";

import { LoadingButton } from "ui";

import { useGetVersionQuery } from "fond/api";
import { AsyncOperationState } from "fond/async/redux";
import { addNotification } from "fond/notifications/redux";
import { PolygonStatus } from "fond/project/polygon";
import { PolygonState, retryDownload, selectDrawExistingOption, selectDrawNewOption } from "fond/project/polygon/redux";
import { closePolygonSelect } from "fond/project/redux";
import { openBaseDataFlyout } from "fond/redux/baseDataFlyout";
import { Store } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

import { getBoundaryWarning, getImportAreaWarning, getImportCompanion } from "./flyout/boundary";
import { useBeginBoundarySelect, useBeginImportAreaSelect, useSaveBoundary, useUploadImportArea } from "./flyout/useBoundary";
import { AREA_DOWNLOAD_ERROR, BOUNDARY_SAVE_ERROR } from "./copy";
import { BASE_DATA_LAYERS } from "./layers";
import { BASE_DATA_POLYGON_DRAW_SCREEN, PolygonDrawParams } from "./polygonDraw";

const HINT = "Click to add corners; double-click to finish";

const PolygonDraw: React.FC<{ params: PolygonDrawParams }> = ({ params }) => {
  const dispatch = useAppDispatch();
  const store = useStore<Store>();
  const { goBack } = useStackedNavigationContext();
  const versionId = useAppSelector((state) => state.project.versionId);
  const polygon = useAppSelector((state) => state.project.polygon);
  const { data: version } = useGetVersionQuery(versionId, { skip: !versionId });
  const beginBoundarySelect = useBeginBoundarySelect();
  const beginImportAreaSelect = useBeginImportAreaSelect();
  const { saveBoundary, isSaving } = useSaveBoundary();
  const uploadImportArea = useUploadImportArea();
  const [hasStarted, setHasStarted] = useState(false);
  const paramsRef = useRef(params);
  const uploadedAreaRef = useRef(version?.UploadedArea);
  /** Whether the drawn area stays selected for the flyout to confirm the layers to import. */
  const keepsAreaRef = useRef(false);

  const isBoundary = params.purpose === "boundary";
  const title = isBoundary ? "Draw boundary" : "Draw import area";
  const isComplete = hasStarted && polygon.isOpen && polygon.polygonState === PolygonState.complete;
  // Only an import area needs its FOND data downloaded before Finish; a boundary is saved without it.
  const hasDownloadFailed = !isBoundary && isComplete && polygon.downloadState === AsyncOperationState.failure && !polygon.status;
  let warning: string | null = null;
  if (isComplete) warning = params.purpose === "boundary" ? getBoundaryWarning(polygon) : getImportAreaWarning(polygon, params.layerKey);
  const canFinish = isBoundary
    ? isComplete
    : isComplete && polygon.downloadState === AsyncOperationState.success && polygon.status === PolygonStatus.ValidArea && !warning;

  // Begin drawing once, when the screen opens, starting from the boundary when there is one; leave polygon select when it closes,
  // unless the area's data is being imported or confirmed.
  useEffect(() => {
    const { current } = paramsRef;
    if (current.purpose === "boundary") {
      beginBoundarySelect();
    } else {
      beginImportAreaSelect(current.layerKey);
    }
    const uploadedArea = uploadedAreaRef.current;
    dispatch(uploadedArea ? selectDrawExistingOption(uploadedArea) : selectDrawNewOption());
    setHasStarted(true);
    return () => {
      const { isOpen, polygonState } = store.getState().project.polygon;
      if (isOpen && polygonState !== PolygonState.upload && !keepsAreaRef.current) dispatch(closePolygonSelect());
    };
  }, [beginBoundarySelect, beginImportAreaSelect, dispatch, store]);

  const handleCancel = () => {
    dispatch(closePolygonSelect());
    goBack();
  };

  const handleFinish = async () => {
    if (params.purpose === "importArea") {
      // A layer imported with a companion layer confirms what to import in the flyout.
      if (getImportCompanion(params.layerKey)) {
        keepsAreaRef.current = true;
      } else {
        uploadImportArea(params.layerKey, "drawnAreaImport", [params.layerKey]);
      }
      goBack();
      dispatch(openBaseDataFlyout({ target: params.layerKey, expanded: "importLayer" }));
      return;
    }
    try {
      await saveBoundary(polygon.selectedArea, "boundarySave");
    } catch {
      dispatch(addNotification(BOUNDARY_SAVE_ERROR, { variant: "error" }));
      return;
    }
    dispatch(closePolygonSelect());
    dispatch(addNotification("Boundary saved."));
    goBack();
    dispatch(openBaseDataFlyout({ target: "boundary", expanded: "importWithinBoundary" }));
  };

  return (
    <>
      <StackedNavigationHeader onBackButtonClick={handleCancel} title={title} />
      <Box display="flex" flexDirection="column" gap={1}>
        <Alert severity="info">
          <AlertTitle sx={{ mb: isBoundary ? 0 : undefined }}>{HINT}</AlertTitle>
          {params.purpose === "importArea" && `FOND ${BASE_DATA_LAYERS[params.layerKey].noun} inside the area are imported.`}
        </Alert>
        {hasDownloadFailed && (
          <Alert
            severity="error"
            action={
              <Button size="small" onClick={() => dispatch(retryDownload())}>
                Retry
              </Button>
            }
          >
            {AREA_DOWNLOAD_ERROR}
          </Alert>
        )}
        {warning && <Alert severity="warning">{warning}</Alert>}
      </Box>
      <Divider sx={{ mt: 3 }} />
      <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
        <Button color="primary" size="small" onClick={handleCancel} disabled={isSaving}>
          Cancel
        </Button>
        <LoadingButton variant="contained" size="small" onClick={handleFinish} sx={{ ml: 1, px: 2 }} loading={isSaving} disabled={!canFinish}>
          Finish
        </LoadingButton>
      </Box>
    </>
  );
};

/**
 * Stacked screen for drawing the boundary, or an area to import a layer's FOND data from, with polygon select.
 */
const PolygonDrawScreen: React.FC = () => {
  const { params } = useStackedNavigationContext();
  const drawParams = params[BASE_DATA_POLYGON_DRAW_SCREEN] as PolygonDrawParams | undefined;
  return drawParams ? <PolygonDraw params={drawParams} /> : null;
};

export default PolygonDrawScreen;
